/**
 * HR/Owner — ดูประวัติการเข้างานของพนักงานรายคนทั้งช่วง
 * (มา/ขาด/ลา + เข้า-ออกกี่โมงรายวัน + สาย). เลือกพนักงาน + ช่วงเวลา.
 * ใช้ AttendanceTimeline ร่วมกับหน้า "ของฉัน".
 */
import { useState } from 'react';
import { User, Download, Users } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useEmployees, useEmployeeAttendance } from '../../hooks';
import { EmployeePicker } from '../../components/ui/EmployeePicker';
import { apiDownload } from '../../lib/api-client';
import { useAuthStore } from '../../lib/store';
import {
  AttendanceTimeline,
  AttendanceRangePresets,
  rangeForPreset,
  thisMonthRange,
  type RangePreset,
} from '../../components/domain/AttendanceTimeline';

interface EmpOpt {
  emp_code: string;
  first_name?: string | null;
  last_name?: string | null;
  nickname?: string | null;
  status?: string | null;
  department?: string | null;
  position?: string | null;
}

export function EmployeeAttendance() {
  const { data: employees } = useEmployees() as { data: EmpOpt[] | undefined };
  const [empCode, setEmpCode] = useState<string>('');
  const [range, setRange] = useState(thisMonthRange);
  const [preset, setPreset] = useState<RangePreset>('this-month');
  const { data, isLoading } = useEmployeeAttendance(empCode || null, range);

  // ── ดาวน์โหลดเวลาเข้า-ออก "ทุกคน" ในช่วงเดียว (CSV จาก worker) ──
  const [bulkFrom, setBulkFrom] = useState(() => thisMonthRange().from);
  const [bulkTo, setBulkTo] = useState(() => thisMonthRange().to);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkErr, setBulkErr] = useState<string | null>(null);
  // รวมคนลาออก = ข้อมูลส่วนบุคคล → เฉพาะ OWNER/HR
  const role = useAuthStore((s) => s.profile?.role ?? 'EMPLOYEE');
  const canSeeResigned = role === 'OWNER' || role === 'HR';
  const [inclResigned, setInclResigned] = useState(false);
  async function exportAllCsv() {
    setBulkErr(null); setBulkBusy(true);
    try {
      const resignedQ = canSeeResigned && inclResigned ? '&include_resigned=1' : '';
      await apiDownload(`/owner/export/attendance?from=${bulkFrom}&to=${bulkTo}${resignedQ}`, `เวลาเข้างาน_ทุกคน_${bulkFrom}_ถึง_${bulkTo}.csv`);
    } catch {
      setBulkErr('ดาวน์โหลดไม่สำเร็จ ลองใหม่อีกครั้ง');
    } finally { setBulkBusy(false); }
  }

  function applyPreset(p: RangePreset) {
    setPreset(p);
    setRange(rangeForPreset(p));
  }

  const opts = (employees ?? []).filter((e) => e.status === 'active' || e.status === 'probation');

  const selectedEmp = opts.find((e) => e.emp_code === empCode);
  const empName = selectedEmp
    ? (`${selectedEmp.first_name ?? ''} ${selectedEmp.last_name ?? ''}`.trim() || selectedEmp.nickname || empCode)
    : empCode;

  const STATUS_TH: Record<string, string> = { working: 'ทำงาน', short_work: 'งานยังไม่ครบ', on_leave: 'ลา', absent: 'ขาด', holiday: 'หยุด' };
  function timeAt(iso?: string): string {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString('en-GB', { timeZone: 'Asia/Bangkok', hour: '2-digit', minute: '2-digit' });
  }
  // ดาวน์โหลดเวลาเข้า-ออกของพนักงานที่เลือก (ทั้งช่วง) เป็น Excel
  async function exportExcel() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const days = (data as any)?.days as any[] | undefined;
    if (!days?.length) return;
    const header = ['วันที่', 'สถานะ', 'กะ', 'เข้า', 'พักออก', 'พักเข้า', 'เลิก', 'สาย(นาที)', 'หมายเหตุ'];
    const rows = days.map((d) => {
      const ev = d.events || {};
      const note = d.holiday ? `วันหยุด: ${d.holiday}` : d.leave ? `ลา ${d.leave.type}${d.leave.reason ? ` (${d.leave.reason})` : ''}` : '';
      return [
        d.date,
        STATUS_TH[d.status] ?? d.status ?? '',
        d.shift_code ?? '',
        timeAt(ev.morning_in?.at),
        timeAt(ev.lunch_out?.at),
        timeAt(ev.lunch_in?.at),
        timeAt(ev.evening_out?.at),
        ev.morning_in?.late_minutes || 0,
        note,
      ];
    });
    const XLSX = await import('xlsx');
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    ws['!cols'] = [{ wch: 12 }, { wch: 12 }, { wch: 8 }, { wch: 7 }, { wch: 7 }, { wch: 7 }, { wch: 7 }, { wch: 9 }, { wch: 24 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'เวลาเข้างาน');
    XLSX.writeFile(wb, `เวลาเข้างาน_${empName}_${range.from}_ถึง_${range.to}.xlsx`);
  }

  return (
    <AdminLayout title="ประวัติเข้างานรายคน">
      {/* ดาวน์โหลดเวลาเข้า-ออกของพนักงานทุกคนในครั้งเดียว (CSV) */}
      <div className="card p-3 mb-3">
        <div className="flex items-center gap-2 mb-2">
          <Users className="w-4 h-4 text-muted" />
          <span className="font-semibold text-sm">ดาวน์โหลดเวลาเข้า-ออก — ทุกคน</span>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="label">ตั้งแต่วันที่</label>
            <input type="date" value={bulkFrom} max={bulkTo} onChange={(e) => setBulkFrom(e.target.value)} className="input" />
          </div>
          <div>
            <label className="label">ถึงวันที่</label>
            <input type="date" value={bulkTo} min={bulkFrom} onChange={(e) => setBulkTo(e.target.value)} className="input" />
          </div>
          <button onClick={exportAllCsv} disabled={bulkBusy || !bulkFrom || !bulkTo} className="btn-primary btn-slim inline-flex items-center gap-1">
            <Download className="w-4 h-4" /> {bulkBusy ? 'กำลังดึง…' : 'ดาวน์โหลด CSV ทุกคน'}
          </button>
        </div>
        {canSeeResigned && (
          <label className="flex items-center gap-2 mt-2 text-sm text-muted cursor-pointer">
            <input type="checkbox" checked={inclResigned} onChange={(e) => setInclResigned(e.target.checked)} />
            รวมคนที่พ้นสภาพ/ลาออกในรอบนี้ด้วย (เฉพาะคนที่มีสแกน/ลาจริง — มีป้าย “พ้นสภาพ” ท้ายชื่อ)
          </label>
        )}
        <p className="text-xs text-muted mt-2">ได้ไฟล์เดียว — 1 แถวต่อคนต่อวัน (รหัส · ชื่อ · แผนก · วันที่ · เวลาเข้า · เวลาออก · สาย · หมายเหตุ) เปิดด้วย Excel ได้</p>
        {bulkErr && <p className="text-xs text-red-500 mt-1">{bulkErr}</p>}
      </div>

      <div className="card p-3 mb-3">
        <label className="label">เลือกพนักงาน (ดูรายคน)</label>
        <EmployeePicker employees={opts} value={empCode || null} onChange={(c) => setEmpCode(c ?? '')} />
      </div>

      {!empCode ? (
        <div className="card p-6 text-center text-gray-400">
          <User className="w-10 h-10 mx-auto mb-2 text-muted" />
          <p className="text-sm">เลือกพนักงานเพื่อดูประวัติการเข้างาน</p>
        </div>
      ) : (
        <>
          <AttendanceRangePresets preset={preset} onChange={applyPreset} />
          <div className="flex justify-end my-2">
            <button
              onClick={exportExcel}
              disabled={isLoading || !((data as unknown as { days?: unknown[] })?.days?.length)}
              className="btn-secondary btn-slim"
              title="ดาวน์โหลดเวลาเข้า-ออกทั้งช่วงเป็นไฟล์ Excel"
            >
              <Download className="w-4 h-4" /> ดาวน์โหลด Excel
            </button>
          </div>
          <AttendanceTimeline data={data} isLoading={isLoading} />
        </>
      )}
    </AdminLayout>
  );
}
